import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
} from '@mui/material';
import { Subcategoria, CreateSubcategoriaInput } from '../types/categoria.types';

interface SubcategoriaFormProps {
  open: boolean;
  subcategoria?: Subcategoria | null;
  loading?: boolean;
  error?: string | null;
  onSave: (data: CreateSubcategoriaInput) => Promise<void>;
  onClose: () => void;
}

export function SubcategoriaForm({
  open,
  subcategoria,
  loading = false,
  error,
  onSave,
  onClose,
}: SubcategoriaFormProps) {
  const [formData, setFormData] = useState<CreateSubcategoriaInput>({
    nombre: '',
    descripcion: '',
    orden: 0,
  });
  const [activo, setActivo] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (subcategoria) {
      setFormData({
        nombre: subcategoria.nombre,
        descripcion: subcategoria.descripcion || '',
        orden: subcategoria.orden,
      });
      setActivo(subcategoria.activo);
    } else {
      setFormData({
        nombre: '',
        descripcion: '',
        orden: 0,
      });
      setActivo(true);
    }
    setValidationErrors({});
  }, [subcategoria, open]);

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.nombre.trim()) {
      errors.nombre = 'Nombre es requerido';
    }

    if (typeof formData.orden === 'number' && formData.orden < 0) {
      errors.orden = 'Orden no puede ser negativa';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (field: keyof CreateSubcategoriaInput, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (validationErrors[field]) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    try {
      const submitData = {
        ...formData,
        activo,
      } as any;
      await onSave(submitData);
      onClose();
    } catch (err) {
      // Error is handled by parent component
    } finally {
      setSubmitting(false);
    }
  };

  const isEditing = !!subcategoria;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? 'Editar Subcategoría' : 'Nueva Subcategoría'}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stack spacing={2}>
          {/* Nombre */}
          <TextField
            label="Nombre"
            value={formData.nombre}
            onChange={(e) => handleChange('nombre', e.target.value)}
            fullWidth
            error={!!validationErrors.nombre}
            helperText={validationErrors.nombre}
            disabled={submitting}
            required
          />

          {/* Descripción */}
          <TextField
            label="Descripción"
            value={formData.descripcion}
            onChange={(e) => handleChange('descripcion', e.target.value)}
            fullWidth
            multiline
            rows={2}
            disabled={submitting}
          />

          {/* Orden */}
          <TextField
            label="Orden"
            value={formData.orden}
            onChange={(e) => handleChange('orden', parseInt(e.target.value) || 0)}
            fullWidth
            type="number"
            inputProps={{ min: 0 }}
            error={!!validationErrors.orden}
            helperText={validationErrors.orden}
            disabled={submitting}
          />

          {/* Activo */}
          {isEditing && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={activo}
                  onChange={(e) => setActivo(e.target.checked)}
                  disabled={submitting}
                />
              }
              label="Activo"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting || loading}
        >
          {submitting && <CircularProgress size={20} sx={{ mr: 1 }} />}
          {isEditing ? 'Actualizar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
